import type { AdminGame, AdminGameVersion, UpdateGameVersionRequest } from '@mcgames/contracts';
import { Badge, Button, TextInput } from '@mcgames/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText } from './ErrorText.tsx';
import styles from './Games.module.css';
import type { InstallDraft } from './install-draft.ts';
import { VersionInstallForm } from './VersionInstallForm.tsx';
import { VersionNzbCard } from './VersionNzbCard.tsx';
import { useVersionMutation } from './version-hooks.ts';

/** The API's limit for a version name. */
const MAX_NAME_LENGTH = 100;

export type VersionEditorProps = {
  game: AdminGame;
  version: AdminGameVersion;
  /** The unsaved install edits of this version; `null` when there are none. */
  draft: InstallDraft | null;
  onDraftChange: (draft: InstallDraft | null) => void;
};

/** Everything about one version: its name, the broken flag, its NZB and how it installs. */
export function VersionEditor({ game, version, draft, onDraftChange }: VersionEditorProps) {
  return (
    <div className={styles['versionEditor']}>
      <VersionName key={version.name} game={game} version={version} />
      {version.broken && <BrokenNotice game={game} version={version} />}
      <VersionNzbCard game={game} version={version} />
      <VersionInstallForm
        game={game}
        version={version}
        draft={draft}
        onDraftChange={onDraftChange}
      />
    </div>
  );
}

type VersionProps = { game: AdminGame; version: AdminGameVersion };

function VersionName({ game, version }: VersionProps) {
  const { t } = useTranslation('admin');
  const save = useVersionMutation<UpdateGameVersionRequest>(game.id, version.id, 'PATCH');
  const [name, setName] = useState(version.name);
  const trimmed = name.trim();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (trimmed !== '') save.mutate({ name: trimmed });
  }

  return (
    <form className={styles['form']} onSubmit={submit}>
      <div className={styles['row']}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.versions.name')}
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <Button
          type="submit"
          variant="secondary"
          disabled={trimmed === '' || trimmed === version.name || save.isPending}
        >
          {t('games.versions.saveName')}
        </Button>
      </div>
      <ErrorText error={save.error} />
    </form>
  );
}

/** Players reported the version's download as broken; it is not offered until it is cleared. */
function BrokenNotice({ game, version }: VersionProps) {
  const { t } = useTranslation('admin');
  const clear = useVersionMutation<UpdateGameVersionRequest>(game.id, version.id, 'PATCH');

  return (
    <div className={styles['brokenNotice']}>
      <div className={styles['row']}>
        <Badge tone="danger">{t('games.versions.broken')}</Badge>
        <Button
          size="sm"
          variant="secondary"
          disabled={clear.isPending}
          onClick={() => clear.mutate({ broken: false })}
        >
          {t('games.versions.markWorking')}
        </Button>
      </div>
      <p className={styles['hint']}>{t('games.versions.brokenHint')}</p>
      <ErrorText error={clear.error} />
    </div>
  );
}
